import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useI18n } from '../lib/i18n';

interface Props {
  offerId: string;
  /** Změna tohoto klíče vynutí reload iframu — používáme `updatedAt` nabídky. */
  version: number | string;
}

/**
 * Náhled dokumentu. Vykresluje přímo HTML z backendu (`/api/offers/:id/preview`),
 * takže se náhled nemůže rozejít s tím, co jde do PDF.
 */
export default function OfferPreview({ offerId, version }: Props) {
  const { t } = useI18n();
  const ref = useRef<HTMLIFrameElement>(null);
  const [overflowPages, setOverflowPages] = useState<number[]>([]);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;

    const onLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      // A4 na 96 dpi = 1123 px. Stránka, která je výrazně vyšší, se v PDF
      // zalomí na další list — na to chceme uživatele upozornit dřív, než pošle
      // nabídku klientovi.
      const A4_PX = 1123;
      const over: number[] = [];
      doc.querySelectorAll('section.page').forEach((page, index) => {
        if ((page as HTMLElement).offsetHeight > A4_PX + 8) over.push(index + 1);
      });
      setOverflowPages(over);
    };

    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [version]);

  return (
    <div className="flex h-full flex-col">
      {overflowPages.length > 0 && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-xs text-dark">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
          <span>{t('preview.overflow', { pages: overflowPages.join(', ') })}</span>
        </div>
      )}
      <iframe
        ref={ref}
        title={t('preview.title')}
        src={`/api/offers/${offerId}/preview?v=${version}`}
        className="h-full w-full rounded-lg border border-gray-200 bg-white"
      />
    </div>
  );
}
